/*
 * Aurelliani product page helpers (own code).
 * Custom elements used by the au-* product blocks and sections:
 *   au-countdown, au-pdp-modal (size guide dialog + info side panels),
 *   au-variant-sync (low stock), au-preorder, au-back-in-stock,
 *   au-sticky-atc, au-complete-look, au-recently-viewed.
 * Hooks into Horizon 4 through Shopify's standard events: 'shopify:product:select'
 * (ProductSelectEvent, its promise carries the re-rendered section) for variant
 * changes, and Shopify.actions.updateCart for the complete-the-look add, so the
 * cart drawer, bubble and cart items refresh like a normal add to cart.
 */
import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const define = (name, element) => {
  if (!customElements.get(name)) customElements.define(name, element);
};

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ESCAPES[c]);

const imgUrl = (src, width) => {
  if (!src) return '';
  const s = String(src);
  return `${s}${s.includes('?') ? '&' : '?'}width=${width}`;
};

const money = (el, cents) =>
  formatMoney(Number(cents) || 0, el.dataset.moneyFormat || '${{amount}}', el.dataset.currency || 'USD');

/**
 * Listens for Horizon's variant change inside the element's section.
 * Ignores events from other products (quick add modals, featured products).
 * @returns {() => void} remove the listener
 */
function onVariantUpdate(el, handler) {
  const scope = el.closest('.shopify-section') || document;
  const listener = (event) => {
    const promise = event.promise;
    if (!promise || typeof promise.then !== 'function') return;
    promise
      .then((result) => {
        const d = result && result.detail;
        if (!d) return;
        const data = { html: d.html, productId: d.productId, newProduct: d.newProduct };
        const own = el.dataset.productId;
        if (own && !data.newProduct && data.productId && String(data.productId) !== own) return;
        // Same shape the handlers used with Horizon 3's variant:update event.
        handler({ resource: d.resource, data });
      })
      .catch(() => {});
  };
  scope.addEventListener(StandardEvents.productSelect, listener);
  return () => scope.removeEventListener(StandardEvents.productSelect, listener);
}

/**
 * Adds one variant through Shopify's standard cart action. Horizon routes the
 * resulting lines-update event from the cart drawer, which refreshes and opens it.
 * @returns {Promise<string>} empty on success, else the error message
 */
async function addVariant(variantId, source) {
  const actions = window.Shopify?.actions;
  if (actions?.updateCart) {
    const result = await actions.updateCart(
      { lines: [{ merchandiseId: String(variantId), quantity: 1 }] },
      { event: { context: 'product', detail: { source } } }
    );
    if (result?.userErrors?.length) return result.userErrors[0].message || 'error';
    if (!document.querySelector('cart-drawer-component[auto-open]')) actions.openCart?.().catch(() => {});
    return '';
  }
  if (window.AU_CART) {
    try {
      await window.AU_CART.add(variantId, 1, source);
      return '';
    } catch (error) {
      return error?.description || 'error';
    }
  }
  return 'error';
}

/* ---------- scroll lock + focus trap for dialogs and side panels ---------- */
let locks = 0;
let previousOverflow = '';
function lockScroll(on) {
  const root = document.documentElement;
  if (on) {
    if (locks === 0) {
      previousOverflow = root.style.overflow;
      root.style.overflow = 'hidden';
      root.setAttribute('scroll-lock', ''); // Horizon 4 also locks .page-wrapper with this
    }
    locks += 1;
  } else if (locks > 0) {
    locks -= 1;
    if (locks === 0) {
      root.style.overflow = previousOverflow;
      root.removeAttribute('scroll-lock');
    }
  }
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function trapTab(container, event) {
  if (event.key !== 'Tab') return;
  const items = [...container.querySelectorAll(FOCUSABLE)].filter((node) => node.getClientRects().length);
  if (!items.length) {
    event.preventDefault();
    return;
  }
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;
  if (event.shiftKey && (active === first || !container.contains(active))) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

/* ---------- au-pdp-modal: a trigger + a native <dialog> (centred or side panel) ---------- */
class AuPdpModal extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog || this.bound) return;
    this.bound = true;
    this.triggers = [...this.querySelectorAll('[data-au-open]')];
    this.triggers.forEach((t) => t.addEventListener('click', this.open));
    this.dialog.addEventListener('click', this.onClick);
    this.dialog.addEventListener('cancel', this.onCancel);
    this.dialog.addEventListener('close', this.onClosed);
    this.dialog.addEventListener('keydown', this.onKey);
  }

  disconnectedCallback() {
    if (this.locked) {
      lockScroll(false);
      this.locked = false;
    }
  }

  open = (event) => {
    if (!this.dialog || this.dialog.open) return;
    this.lastTrigger = event?.currentTarget || null;
    this.dialog.showModal();
    lockScroll(true);
    this.locked = true;
    this.setExpanded(true);
    const focusTarget = this.dialog.querySelector('[data-au-close]');
    focusTarget?.focus({ preventScroll: true });
    requestAnimationFrame(() => requestAnimationFrame(() => this.dialog.setAttribute('data-open', '')));
  };

  close = () => {
    if (!this.dialog.open || this.closing) return;
    this.closing = true;
    this.dialog.removeAttribute('data-open');
    window.setTimeout(() => {
      this.closing = false;
      if (this.dialog.open) this.dialog.close();
    }, reducedMotion() ? 0 : 300);
  };

  onClosed = () => {
    this.dialog.removeAttribute('data-open');
    this.closing = false;
    if (this.locked) {
      lockScroll(false);
      this.locked = false;
    }
    this.setExpanded(false);
    this.lastTrigger?.focus({ preventScroll: true });
  };

  onClick = (event) => {
    if (event.target === this.dialog || event.target.closest('[data-au-close]')) this.close();
  };

  onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  onKey = (event) => trapTab(this.dialog, event);

  setExpanded(value) {
    this.triggers?.forEach((t) => t.setAttribute('aria-expanded', String(value)));
  }
}

/* ---------- au-countdown ---------- */
function parseEnd(raw, tz) {
  const m = String(raw || '')
    .trim()
    .match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?/);
  if (!m) return null;
  const z = String(tz || '')
    .trim()
    .replace(/^(UTC|GMT)/i, '')
    .match(/^([+-])?(\d{1,2})(?::?(\d{2}))?$/);
  let offset = 0;
  if (z) offset = (z[1] === '-' ? -1 : 1) * (Number(z[2]) * 60 + Number(z[3] || 0));
  const hours = m[4] === undefined ? 23 : Number(m[4]);
  const minutes = m[5] === undefined ? 59 : Number(m[5]);
  const utc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), hours, minutes) - offset * 60000;
  return Number.isFinite(utc) ? utc : null;
}

class AuCountdown extends HTMLElement {
  connectedCallback() {
    this.end = parseEnd(this.dataset.end, this.dataset.tz);
    this.nums = {};
    this.querySelectorAll('[data-unit]').forEach((n) => {
      this.nums[n.dataset.unit] = n;
    });
    if (this.end === null) {
      this.finish();
      return;
    }
    this.tick();
    this.timer = window.setInterval(this.tick, 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  tick = () => {
    const left = this.end - Date.now();
    if (left <= 0) {
      this.finish();
      return;
    }
    const s = Math.floor(left / 1000);
    this.set('d', Math.floor(s / 86400));
    this.set('h', Math.floor((s % 86400) / 3600));
    this.set('m', Math.floor((s % 3600) / 60));
    this.set('s', s % 60);
    this.hidden = false;
  };

  set(unit, value) {
    const node = this.nums[unit];
    if (!node) return;
    const text = String(value).padStart(2, '0');
    if (node.textContent !== text) node.textContent = text;
  }

  finish() {
    window.clearInterval(this.timer);
    if (this.hasAttribute('data-design')) {
      ['d', 'h', 'm', 's'].forEach((u) => this.set(u, 0));
      this.setAttribute('data-ended', '');
      this.hidden = false;
    } else {
      this.hidden = true;
    }
  }
}

/* ---------- au-variant-sync: swaps its markup with the copy from the new variant's page ---------- */
class AuVariantSync extends HTMLElement {
  connectedCallback() {
    this.off = onVariantUpdate(this, this.update);
  }

  disconnectedCallback() {
    this.off?.();
  }

  update = (detail) => {
    const key = this.dataset.auSync;
    const html = detail.data.html;
    if (!key || !html || typeof html.querySelector !== 'function') return;
    const fresh = html.querySelector(`[data-au-sync="${CSS.escape(key)}"]`);
    if (!fresh) return;
    this.innerHTML = fresh.innerHTML;
    this.hidden = fresh.hasAttribute('hidden');
  };
}

/* ---------- au-preorder: relabels the theme's add to cart button ---------- */
class AuPreorder extends HTMLElement {
  connectedCallback() {
    this.label = this.dataset.label || 'Pre-order';
    this.scope = this.closest('.shopify-section') || document;
    this.apply();
    this.off = onVariantUpdate(this, () => requestAnimationFrame(this.apply));
    this.observer = new MutationObserver(this.apply);
    this.scope
      .querySelectorAll('product-form-component')
      .forEach((form) => this.observer.observe(form, { childList: true, subtree: true, characterData: true }));
  }

  disconnectedCallback() {
    this.off?.();
    this.observer?.disconnect();
  }

  apply = () => {
    this.scope.querySelectorAll('product-form-component [ref="addToCartButton"]').forEach((button) => {
      if (button.disabled) return;
      const text =
        button.querySelector('.add-to-cart-text__content span span') ||
        button.querySelector('.add-to-cart-text__content > span');
      if (text) {
        if (text.textContent.trim() !== this.label) text.textContent = this.label;
        return;
      }
      // Horizon 4: the label is a bare text node inside the button.
      const node = [...button.childNodes].find((n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim());
      if (node && node.textContent.trim() !== this.label) node.textContent = ` ${this.label} `;
    });
  };
}

/* ---------- au-back-in-stock ---------- */
class AuBackInStock extends HTMLElement {
  connectedCallback() {
    this.off = onVariantUpdate(this, this.update);
  }

  disconnectedCallback() {
    this.off?.();
  }

  update = (detail) => {
    if (this.querySelector('.au-bis__thanks')) return;
    const variant = detail.resource;
    const ask = this.querySelector('.au-bis__ask');
    if (ask) ask.hidden = !(variant && variant.available === false);
    if (!variant) return;
    const title = variant.public_title || variant.title || '';
    const url = `${window.location.origin}${window.location.pathname}?variant=${variant.id}`;
    this.setField('[data-au-variant]', title);
    this.setField('[data-au-variant-id]', variant.id);
    this.setField('[data-au-url]', url);
    this.setField(
      '[data-au-body]',
      `${this.dataset.bodyPrefix || 'Back in stock request'}: ${this.dataset.productTitle || ''}${title ? ` / ${title}` : ''} / ${url}`
    );
  };

  setField(selector, value) {
    const field = this.querySelector(selector);
    if (field) field.value = String(value);
  }
}

/* ---------- au-sticky-atc ---------- */
class AuStickyAtc extends HTMLElement {
  connectedCallback() {
    this.bar = this.bar || this.querySelector('.au-sticky-atc');
    if (!this.bar) return;
    this.scope = this.closest('.shopify-section') || document;
    this.form = this.scope.querySelector('product-form-component');
    this.off = onVariantUpdate(this, this.update);
    if (!this.button) {
      this.chosen =
        !this.hasAttribute('data-require-choice') || new URL(window.location.href).searchParams.has('variant');
      this.button = this.bar.querySelector('[data-au-sticky-btn]');
      this.button?.addEventListener('click', this.onClick);
    }
    /* live at the end of <body> so no transformed ancestor can trap the fixed bar */
    document.body.appendChild(this.bar);

    if (this.hasAttribute('data-design')) {
      this.show(true);
      return;
    }
    const target = this.form?.querySelector('.product-form-buttons, [ref="addToCartButton"]');
    if (!target || !('IntersectionObserver' in window)) return;
    this.target = target;
    this.past = false;
    this.footerIn = false;
    this.io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === this.target) {
          this.past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        } else {
          this.footerIn = entry.isIntersecting;
        }
      });
      this.show(this.past && !this.footerIn);
    });
    this.io.observe(target);
    const footer = document.querySelector('.shopify-section-group-footer-group, footer');
    if (footer) this.io.observe(footer);
  }

  disconnectedCallback() {
    this.off?.();
    this.io?.disconnect();
    this.bar?.remove();
  }

  show(on) {
    this.bar.classList.toggle('is-visible', on);
    this.bar.toggleAttribute('inert', !on);
    this.bar.setAttribute('aria-hidden', String(!on));
  }

  update = (detail) => {
    this.chosen = true;
    const html = detail.data.html;
    const fresh =
      html && typeof html.querySelector === 'function'
        ? html.querySelector(`[data-au-sticky="${CSS.escape(this.dataset.block || '')}"]`)
        : null;
    if (fresh) {
      ['[data-au-sticky-media]', '[data-au-sticky-variant]', '[data-au-sticky-price]', '[data-au-sticky-btn]'].forEach(
        (selector) => {
          const now = this.bar.querySelector(selector);
          const next = fresh.querySelector(selector);
          if (!now || !next) return;
          now.innerHTML = next.innerHTML;
          if (now instanceof HTMLButtonElement) now.disabled = next.hasAttribute('disabled');
        }
      );
    } else if (this.button) {
      this.button.disabled = !(detail.resource && detail.resource.available);
    }
  };

  onClick = () => {
    const smooth = reducedMotion() ? 'auto' : 'smooth';
    const goTo = (el) => el?.scrollIntoView({ behavior: smooth, block: 'center' });
    if (!this.chosen) {
      goTo(this.scope.querySelector('variant-picker') || this.form);
      this.chosen = true;
      return;
    }
    const mainButton = this.form?.querySelector('[ref="addToCartButton"]');
    if (!mainButton || mainButton.disabled) {
      goTo(this.form);
      return;
    }
    /* the theme's own button: same validation, properties, quantity, cart drawer and events */
    mainButton.click();
  };
}

/* ---------- au-complete-look ---------- */
class AuCompleteLook extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    if (this.hasAttribute('data-complementary')) this.loadComplementary();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  async loadComplementary() {
    const { productId, recUrl } = this.dataset;
    if (!productId || !recUrl) return;
    try {
      const response = await fetch(
        `${recUrl}.json?product_id=${encodeURIComponent(productId)}&limit=4&intent=complementary`
      );
      if (!response.ok) return;
      const json = await response.json();
      const product = (json.products || []).find((p) => String(p.id) !== productId && p.available);
      if (product) this.render(product);
    } catch (error) {
      /* keep the fallback product */
    }
  }

  render(product) {
    const card = this.querySelector('[data-au-look-card]');
    if (!card) return;
    const variants = product.variants || [];
    const variant = variants.find((v) => v.available) || variants[0];
    const single = variants.length === 1 && variant && variant.available;
    const image = product.featured_image
      ? `<img src="${esc(imgUrl(product.featured_image, 200))}" alt="" width="200" height="200" loading="lazy">`
      : '';
    const from = product.price_varies ? `${esc(this.dataset.fromLabel || 'From')} ` : '';
    const addTpl = this.querySelector('template[data-au-tpl-add]')?.innerHTML || '';
    const linkTpl = this.querySelector('template[data-au-tpl-link]')?.innerHTML || '';
    const action = single
      ? `<button type="button" class="au-look__add" data-au-add data-variant-id="${esc(variant.id)}" data-product-id="${esc(product.id)}" aria-label="${esc(`${this.dataset.addLabel || 'Add to cart'}: ${product.title}`)}">${addTpl}</button>`
      : `<a class="au-look__add" href="${esc(product.url)}" aria-label="${esc(`${this.dataset.chooseLabel || 'Choose options'}: ${product.title}`)}">${linkTpl}</a>`;
    card.innerHTML = `
      <a class="au-look__media" href="${esc(product.url)}" tabindex="-1" aria-hidden="true">${image}</a>
      <div class="au-look__info">
        <a class="au-look__title" href="${esc(product.url)}">${esc(product.title)}</a>
        <span class="au-look__price">${from}${esc(money(this, product.price))}</span>
      </div>
      ${action}`;
    this.hidden = false;
  }

  onClick = async (event) => {
    const button = event.target.closest('[data-au-add]');
    if (!button || !this.contains(button)) return;
    event.preventDefault();
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    const message = this.querySelector('[data-au-look-msg]');
    if (message) message.textContent = '';
    try {
      const error = await addVariant(button.dataset.variantId, 'au-complete-look');
      if (error) {
        if (message) message.textContent = error === 'error' ? this.dataset.errorText || '' : error;
        return;
      }
      button.classList.add('is-added');
      if (message) message.textContent = this.dataset.addedText || '';
      window.setTimeout(() => button.classList.remove('is-added'), 2200);
    } catch (error) {
      if (message) message.textContent = this.dataset.errorText || '';
    } finally {
      button.removeAttribute('aria-busy');
    }
  };
}

/* ---------- au-recently-viewed: localStorage au_recent = [handles], newest first ---------- */
class AuRecentlyViewed extends HTMLElement {
  connectedCallback() {
    this.load();
  }

  async load() {
    const current = this.dataset.current || '';
    const max = Number(this.dataset.max) || 8;
    let list = [];
    try {
      list = JSON.parse(window.localStorage.getItem('au_recent') || '[]');
    } catch (error) {
      list = [];
    }
    if (!Array.isArray(list)) list = [];
    list = list.filter((h) => typeof h === 'string' && h);
    if (current) {
      try {
        const next = [current, ...list.filter((h) => h !== current)].slice(0, 12);
        window.localStorage.setItem('au_recent', JSON.stringify(next));
      } catch (error) {
        /* storage blocked: nothing to remember */
      }
    }
    const handles = list.filter((h) => h !== current).slice(0, max);
    if (!handles.length) return;
    const root = (this.dataset.root || '/').replace(/\/$/, '');
    const products = (
      await Promise.all(
        handles.map((h) =>
          fetch(`${root}/products/${encodeURIComponent(h)}.js`)
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null)
        )
      )
    ).filter(Boolean);
    const track = this.querySelector('[data-au-rv-track]');
    if (!products.length || !track || !this.isConnected) return;
    track.innerHTML = products.map((p) => this.card(p)).join('');
    this.hidden = false;
    window.AuReveal?.watch?.(this);
  }

  card(p) {
    const first = p.featured_image || (p.images || [])[0];
    const second = (p.images || []).find((src) => src && src !== first);
    const sale = p.compare_at_price > p.price;
    const sizes = '(min-width: 990px) 22vw, (min-width: 750px) 30vw, 46vw';
    const srcset = (u) => [300, 450, 600, 800].map((w) => `${imgUrl(u, w)} ${w}w`).join(', ');
    const img = (u, cls) =>
      `<img class="${cls}" src="${esc(imgUrl(u, 600))}" srcset="${esc(srcset(u))}" sizes="${sizes}" alt="" width="600" height="600" loading="lazy">`;
    let badge = '';
    if (!p.available) badge = `<span class="au-card__badge">${esc(this.dataset.soldOut || 'Sold out')}</span>`;
    else if (sale) badge = `<span class="au-card__badge">${esc(this.dataset.saleLabel || 'Sale')}</span>`;
    const from = p.price_varies ? `${esc(this.dataset.fromLabel || 'From')} ` : '';
    return `<div class="au-card au-rv__item">
      <a href="${esc(p.url)}" class="au-card__media${second ? ' au-card__media--swap' : ''}" tabindex="-1" aria-hidden="true">
        ${first ? img(first, 'au-card__img') : '<span class="au-ph"></span>'}${second ? img(second, 'au-card__img au-card__img--2') : ''}${badge}
      </a>
      <div class="au-card__info">
        <a href="${esc(p.url)}" class="au-card__title">${esc(p.title)}</a>
        <p class="au-card__price"><span${sale ? ' class="au-card__now"' : ''}>${from}${esc(money(this, p.price))}</span>${
          sale ? `<s class="au-card__was">${esc(money(this, p.compare_at_price))}</s>` : ''
        }</p>
      </div>
    </div>`;
  }
}

define('au-pdp-modal', AuPdpModal);
define('au-countdown', AuCountdown);
define('au-variant-sync', AuVariantSync);
define('au-preorder', AuPreorder);
define('au-back-in-stock', AuBackInStock);
define('au-sticky-atc', AuStickyAtc);
define('au-complete-look', AuCompleteLook);
define('au-recently-viewed', AuRecentlyViewed);
